<template>
  <div class="chat-widget elevated-card date-time-card">
    <div v-if="date_time_selected" style="padding: 0 1.5rem">
      <v-row no-gutters class="selected-title">{{ $vuetify.locale.t('$vuetify.__DateTime.selected') }}</v-row>
      <v-row no-gutters class="date-time-field" align="center" style="height: 2.375rem">
        <v-icon class="selected-icons">mdi-calendar</v-icon>
        <span class="selected-text">{{ date_text }}</span>
      </v-row>
      <v-row no-gutters align="center" style="height: 2.375rem">
        <v-icon class="selected-icons">mdi-clock-outline</v-icon>
        <span class="selected-text">{{ date_time.time }}</span>
      </v-row>
    </div>
    <div v-else style="padding: 0 1.5rem">
      <div style="padding-bottom: 1rem">
        <v-menu v-model="date_picking" transition="slide-y-transition" origin="top center" offset-y bottom nudge-left="18" :close-on-content-click="datetime.range ? false : true">
          <template v-slot:activator="{ props }">
            <v-text-field
              :value="date_text"
              readonly
              density="compact"
              single-line
              variant="outlined"
              dense
              hide-details
              prepend-inner-icon="mdi-calendar"
              :placeholder="$vuetify.locale.t('$vuetify.__DateTime.select_date')"
              v-bind="props"
            ></v-text-field>
          </template>
          <v-card class="date-menu-card">
            <v-date-picker
              v-if="datetime.range"
              v-model="date_time.date"
              hide-header
              multiple="2"
              scrollable
              :min="datetime.date_min"
              :max="datetime.date_max"
              no-title
              @input="is_datepicker_show = false"
            ></v-date-picker>
            <v-date-picker v-else v-model="date_time.date[0]" hide-header scrollable :min="datetime.date_min" :max="datetime.date_max" no-title @input="is_datepicker_show = false"> </v-date-picker>
          </v-card>
        </v-menu>
      </div>
      <div>
        <v-menu ref="time_menu" transition="slide-y-transition" origin="top center" offset-y bottom nudge-left="18" :close-on-content-click="true">
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="date_time.time"
              readonly
              density="compact"
              single-line
              variant="outlined"
              dense
              hide-details
              prepend-inner-icon="mdi-clock-outline"
              :placeholder="$vuetify.locale.t('$vuetify.__DateTime.select_time')"
              v-bind="props"
            ></v-text-field>
          </template>
          <v-card class="date-menu-card" style="padding: 1rem">
            <v-row no-gutters class="time-btn-area">
              <button v-ripple v-for="(time, i) in datetime.time_options" :key="i" class="chat-btn hover-btn text-none time-btn" variant="outlined" @click="date_time.time = time">{{ time }}</button>
            </v-row>
          </v-card>
        </v-menu>
      </div>

      <v-row no-gutters class="d-flex date-time-action">
        <v-spacer></v-spacer>
        <v-btn class="chat-btn text-none" :style="`color: ${config.main_color}`" outlined @click="submitDateTime()">
          <v-icon>mdi-check</v-icon>
          Submit
        </v-btn>
      </v-row>
    </div>
  </div>
</template>

<script setup>
// Import modules
import global_config from '@/../config.json'
import { ref, computed, watch, defineProps } from 'vue'
import { useStore } from 'vuex'
import { useDate } from 'vuetify'

// Init modules
const store = useStore()
const date_vuetify = useDate()

// Props
const { datetime } = defineProps({
  datetime: Object
})

// Data
const config = ref(global_config)
const is_datepicker_show = ref(false)
const date_time_selected = ref(false)
const date_picking = ref(false)
const date_time = ref({ date: [], time: '' })

// Computed
const date_text = computed(() => {
  let text = ''
  if (datetime.range) {
    date_time.value.date.forEach((date, i) => {
      if (i > 0) text = text.concat(' ~ ')
      text = text.concat(date_vuetify.format(date_time.value.date[i], 'keyboardDate'))
    })
  } else text = date_vuetify.format(date_time.value.date[0], 'keyboardDate')
  return text
})

const submitDateTime = () => {
  date_time_selected.value = true
  const datetime_string = date_text.value.concat(',' + date_time.value.time)
  const datetime_value = { date: date_time.value.date, time: date_time.value.time }
  store.dispatch('sendMessageToSocket', { title: datetime_string, value: datetime_value })
}
watch(
  date_time,
  () => {
    if (datetime.range) {
      if (date_time.value.date.length > 1) date_picking.value = false
    }
  },
  { deep: true }
)
</script>

<style scope>
.date-time-card {
  width: 18.1875rem;
  padding: 1rem 0;
}

.date-time-field {
  margin-bottom: 1rem !important;
}

.time-btn-area {
  max-height: 14.75rem;
  overflow-y: overlay;
}

.time-btn {
  color: #195aea !important;
  margin: 0 0.5rem 0.5rem 0;
}

.date-menu-card {
  box-shadow: 0 0.25rem 0.3125rem rgba(0, 0, 0, 0.14), 0 0.0625rem 0.625rem rgba(0, 0, 0, 0.12), 0 0.125rem 0.25rem rgba(0, 0, 0, 0.2) !important;
  border-radius: 16px !important;
}

.date-time-action {
  padding: 1.5rem 0 0 0;
  justify-content: space-between;
  align-items: center;
}

.selected-small-text {
  font-weight: 400;
  font-size: 0.8125rem;
  line-height: 0.875rem;
  color: #828282;
  padding-bottom: 0.25rem;
}

.selected-date-time-text {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.25rem;
  letter-spacing: 0.00625rem;
  color: #1b1b1b;
}

.selected-title {
  margin-bottom: 1rem !important;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.625rem;
  color: #1a1a1a;
}

.selected-icons {
  margin-left: 0.625rem;
  margin-right: 0.5rem;
}

.selected-text {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.25rem;
  letter-spacing: 0.00625rem;
  color: #1b1b1b;
}

/* text field */
.v-text-field input {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.125rem;
  letter-spacing: 0.1px;
  color: #1b1b1b !important;
}

.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state) > .v-input__control > .v-input__slot fieldset {
  color: rgba(0, 0, 0, 0.2) !important;
}

.v-text-field input::placeholder {
  color: #828282 !important;
}

/* time picker */
.vue__time-picker.v-input--is-focused input.has-custom-icon {
  color: rgba(0, 0, 0, 0.2) !important;
}

.vue__time-picker input.has-custom-icon {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.125rem;
  letter-spacing: 0.1px;
  color: #1b1b1b;
  border-radius: 4px;
  padding-left: 2.5rem;
  height: 2.5rem !important;
}

::placeholder {
  color: #828282;
}
</style>
